import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ApiClientError } from 'grabbitbot-legacy-ui'
import { describeError } from '../../api/errorMessages'
import { useKeyedActions } from '../../hooks/useKeyedActions'

/**
 * Shared plumbing for the B6 admin pages (ticket 4b6a6d24, contract
 * `legacy-migration/docs/analysis/2026-09-27-grabbitbot-b6-contract.md`).
 * Mirrors the B4 `botControlActions.ts` pattern (per-key busy/feedback,
 * synchronous in-flight guard) generalized for admin's wider error
 * vocabulary (403, credit's own 409, process-log's 503, product delete
 * confirmation), plus the pieces B4 didn't need: an `?email=` query-param
 * picker every admin-on-a-user page shares, an idempotency-key generator,
 * and a client-side secret redaction pass applied on top of the backend's
 * own (contract: "Never render secret-looking fields even if a regressed
 * backend returns them" — defense in depth, never trust one layer alone).
 *
 * Non-component logic only — the components that use this (`AdminNotice`,
 * `EmailLookupForm`, `ConfirmButton`, `AdminTabs`) live in `adminShared.tsx`
 * (a plain `.ts` file mixing hook/type exports with a `.tsx` sibling's
 * component exports keeps `react-refresh/only-export-components` quiet,
 * same split as B4's `botControlActions.ts` / `BotActionNotice.tsx`).
 */

export interface AdminFeedback {
  tone: 'warning' | 'error' | 'success'
  text: string
}

export function describeAdminError(err: unknown): AdminFeedback {
  if (err instanceof ApiClientError) {
    if (err.status === 403) {
      return { tone: 'error', text: 'Admin access required.' }
    }
    if (err.status === 410) {
      return { tone: 'warning', text: 'No such user, or nothing found for this lookup.' }
    }
    if (err.status === 409 && err.code === 'credit_change_in_progress') {
      return { tone: 'warning', text: 'Another credit change for this user + currency is already in progress.' }
    }
    if (err.status === 409 && err.code === 'bot_action_in_progress') {
      return { tone: 'warning', text: 'Another action is already running for this bot — please wait for it to finish.' }
    }
    if (err.status === 409 && err.code === 'user_has_no_serviceId') {
      return { tone: 'warning', text: 'This user has no service id yet.' }
    }
    if (err.status === 409 && err.code === 'email_not_set') {
      return { tone: 'warning', text: 'Enter an e-mail first.' }
    }
    if (err.status === 409 && err.code === 'user_not_found') {
      return { tone: 'warning', text: 'No such user.' }
    }
    if (err.status === 502) {
      return { tone: 'error', text: 'Outcome unknown — spotbot was unreachable, check status before retrying.' }
    }
    if (err.status === 503 && err.code === 'process_log_not_available') {
      return { tone: 'warning', text: 'Process logs are not available yet for this backend.' }
    }
    if (err.status === 500) {
      return { tone: 'error', text: 'The request failed (bad input, most likely — check every field).' }
    }
    return { tone: 'error', text: describeError(err, 'The request failed') }
  }
  return { tone: 'error', text: describeError(err, 'The request failed') }
}

/** Per-action busy/feedback tracking, keyed — the shared keyed hook
 * (`src/hooks/useKeyedActions.ts`; its synchronous `inFlight` ref check IS
 * the double-click guard) with the admin wording. */
export function useAdminActions() {
  return useKeyedActions(describeAdminError)
}

/** `?email=` is how every legacy admin cross-link scoped a page to one user
 * (`AdminUsersPage`'s "View credit"/"Bot status"/... links) — kept as the
 * single convention here too so a bookmarked/shared admin URL round-trips. */
export function useEmailParam(): [string, (email: string) => void] {
  const [params, setParams] = useSearchParams()
  const email = params.get('email') ?? ''
  const setEmail = useCallback(
    (value: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value) next.set('email', value)
          else next.delete('email')
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )
  return [email, setEmail]
}

/** Fresh per user-initiated submit, reused only for a RETRY of that exact
 * submit (ticket 4b6a6d24: "send an Idempotency-Key ... fresh UUID per
 * user-initiated submit, reused on retry of the SAME submit"). `crypto.
 * randomUUID` is available in every browser this app targets and in
 * jsdom/vitest; no polyfill needed. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

// --- secret redaction (defense in depth) ------------------------------------

const SECRET_KEY_PATTERN = /key|secret|token|password|passwd|pwd|auth|credential/i

/** Deep-clones `value`, replacing any object VALUE whose key name looks
 * secret-like with `"[redacted]"`. Applied to every raw payload blob this
 * app renders (Stripe/Satelit log `params`/`response`, process-log `value`)
 * on top of whatever the backend already redacted — contract: "Never
 * render secret-looking fields even if a regressed backend returns them."
 * A STRING value is returned unchanged (there is nothing to key-match
 * inside opaque prose) — only structured JSON is walked. */
export function redactSecrets(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactSecrets)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      out[key] = SECRET_KEY_PATTERN.test(key) ? '[redacted]' : redactSecrets(v)
    }
    return out
  }
  return value
}

/** Re-runs `redactSecrets` over a value and pretty-prints it — used wherever
 * a page would otherwise render the backend's own pre-formatted
 * `paramsString`/`responseString`/`valuePretty` (those already went through
 * `JSON.stringify` server-side, so re-stringifying the redacted object here
 * is the only way to keep the redaction visible in the rendered text). */
export function redactedJson(value: unknown): string {
  try {
    return JSON.stringify(redactSecrets(value), null, 4)
  } catch {
    return String(value)
  }
}

export function formatMoney(amount: number | null | undefined, currency?: string | null): string {
  const n = Number(amount ?? 0)
  const c = String(currency || '').toLowerCase()
  const precision = c === 'usd' || c === 'credit' || c === '' ? 2 : 8
  const fixed = Number.isFinite(n) ? n.toFixed(precision) : (0).toFixed(precision)
  return currency ? `${fixed} ${currency}` : fixed
}
